import { logger } from '@/shared/utils';
import { useEffect, useRef, useState } from 'react';
import { getLocalUserName } from '../../../utils/helper';
import { useCallkitDataKeys } from '../../../viewmodels';
import PropTypes from 'prop-types';
import { UserProfile } from '@/features/users';
import styles from '../VideoTile/VideoTile.module.scss';

export default function LocalVideoPreview({ isVideoMuted, isPaused = false, avatarClassName }) {
   const videoRef = useRef(null);
   const streamRef = useRef(null);
   const [isPortrait, setIsPortrait] = useState(false);
   const { makeCallDetails } = useCallkitDataKeys(['makeCallDetails']);
   const isVideoCall = makeCallDetails?.isVideoCall || !isVideoMuted || false;
   const [videoReady, setVideoReady] = useState(false);
   const localUserId = getLocalUserName();

   useEffect(() => {
      if (!isVideoCall) return;

      let localStream = null;
      let isMounted = true;

      const startVideo = async () => {
         try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });

            // If component unmounted or dependency changed while waiting, stop immediately
            if (!isMounted) {
               stream.getTracks().forEach(track => track.stop());
               return;
            }

            localStream = stream;
            streamRef.current = stream;
            if (videoRef.current) {
               videoRef.current.srcObject = stream;
            }
         } catch (err) {
            if (isMounted) {
               logger.error('Error accessing local video:', err);
            }
         }
      };

      startVideo();

      return () => {
         isMounted = false;
         if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
         }
         streamRef.current = null;
      };
   }, [isVideoCall]);

   // Pause/resume video element when tab is blurred/focused
   // Pausing the element freezes the last frame (instead of going black)
   useEffect(() => {
      const videoEl = videoRef.current;
      if (!videoEl) return;
      if (isPaused) {
         videoEl.pause();
      } else {
         videoEl.play().catch(err => {
            if (err.name !== 'AbortError') {
               logger.error('Error resuming video:', err);
            }
         });
      }
   }, [isPaused]);

   if (!isVideoCall) return null;

   return (
      <div className={styles.live}>
         <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            onLoadedMetadata={() => {
               if (videoRef.current) {
                  setIsPortrait(videoRef.current.videoHeight > videoRef.current.videoWidth);
               }
            }}
            onPlaying={() => setVideoReady(true)}
            onPause={() => {
               // Don't hide the frozen frame when we intentionally paused
               if (!isPaused) setVideoReady(false);
            }}
            style={{
               width: '100%',
               height: '100%',
               objectFit: isPortrait ? 'contain' : 'cover',
               background: isPortrait ? 'black' : 'transparent',
               opacity: videoReady ? 1 : 0,
            }}
         />
         {!videoReady && (
            <div className={styles.fallback} style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
               <UserProfile userId={localUserId} className={avatarClassName || styles.avatar} />
            </div>
         )}
      </div>
   );
}

LocalVideoPreview.propTypes = {
   isVideoMuted: PropTypes.bool,
   isPaused: PropTypes.bool,
   avatarClassName: PropTypes.string,
};
